import { DOEPSidebarDefinition } from 'app/modules/sidebar/models';
import { IconArrowRight, IconBuildingBank } from '@u21/tabler-icons';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import {
  REPORT_TYPE_PATH_MAP,
  ReportType,
} from 'app/modules/filings/constants';
import { SarStatus } from 'app/modules/fincenSarOld/models';
import { useGetFinCENSAR } from 'app/modules/fincenSAR/queries/useGetFinCENSAR';
import {
  U21Chip,
  U21Subsection,
  U21Spacer,
  U21LoadError,
} from 'app/shared/u21-ui/components';
import { SidebarDataRow } from 'app/modules/sidebar/components/utils/SidebarDataRow';
import { StatusPill } from 'app/modules/fincenSAR/components/StatusPill';
import { formatDate, formatDatetime } from 'app/shared/utils/date';
import { SidebarCommentsCollapsible } from 'app/modules/sidebar/components/SidebarCommentsCollapsible';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { STATUS_TO_COLOR } from 'app/modules/investigations/constants';
import { useSelector } from 'react-redux';
import {
  selectAgent,
  selectHasReadAssignmentsPermissions,
  selectHasReadCasesPermissions,
} from 'app/modules/session/selectors';
import { isQueueAccessible } from 'app/modules/queues/utils';
import {
  selectClassifiedQueuesEnabled,
  selectDLEnabledForSARDetail,
} from 'app/shared/featureFlags/selectors';
import { ArticleDeadlineChip } from 'app/modules/deadlines/components/ArticleDeadlineChip';
import { useQueryClient } from '@tanstack/react-query';
import { FINCEN_SAR_QUERY_KEYS } from 'app/modules/fincenSAR/queries/keys';

interface Props {
  data: DOEPSidebarDefinition['data'];
}

export const SidebarFinCENDoep = (props: Props) => {
  const {
    data: { id, hideActionButton },
  } = props;

  const hasReadCasesPermission = useSelector(selectHasReadCasesPermissions);
  const hasReadAssignmentsPermission = useSelector(
    selectHasReadAssignmentsPermissions,
  );
  const classifiedQueuesEnabled = useSelector(selectClassifiedQueuesEnabled);
  const sessionAgent = useSelector(selectAgent);
  const useSARCacheEndpoint = useSelector(selectDLEnabledForSARDetail);

  const queryClient = useQueryClient();
  const {
    query: { data: filing, isError, isLoading, refetch },
  } = useGetFinCENSAR(id);

  return (
    <U21SideMenu
      actionButtonProps={
        hideActionButton || !filing
          ? undefined
          : {
              children: 'Go to Filing Page',
              endIcon: <IconArrowRight />,
              to: REPORT_TYPE_PATH_MAP[ReportType.FINCEN_DOEP].replace(
                ':id',
                String(id),
              ),
            }
      }
      loading={isLoading}
      noPadding
      title="FinCEN DOEP Details"
    >
      {(() => {
        if (isLoading) {
          return null;
        }

        if (isError) {
          return (
            <U21LoadError label="FinCEN DOEP" onTryAgain={() => refetch()} />
          );
        }

        if (!filing) {
          return null;
        }

        return (
          <U21Spacer spacing={0.5}>
            <U21Subsection
              disableTitleCase
              shadedHeader
              title={filing.name}
              titleIcon={<IconBuildingBank />}
            >
              <U21Spacer>
                <SidebarDataRow label="Assigned to" showEmptyFields>
                  {filing.reviewer?.full_name || filing.reviewer?.email}
                </SidebarDataRow>
                <SidebarDataRow label="Queue" showEmptyFields>
                  {filing.queue?.title}
                </SidebarDataRow>
                <SidebarDataRow label="Status" showEmptyFields>
                  <StatusPill status={filing.status} />
                </SidebarDataRow>
                <SidebarDataRow label="Deadline" showEmptyFields>
                  {filing.status !== SarStatus.ARCHIVED &&
                    !filing.submitted && (
                      <ArticleDeadlineChip
                        articleId={id}
                        deadline={filing.deadline}
                        deadlineConfig={filing.deadline_config}
                        onEditSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: FINCEN_SAR_QUERY_KEYS.getFinCENSAR(
                              id,
                              useSARCacheEndpoint,
                            ),
                          });
                        }}
                      />
                    )}
                </SidebarDataRow>
                <SidebarDataRow label="Case" showEmptyFields>
                  {filing.case && (
                    <U21Chip
                      color={STATUS_TO_COLOR[filing.case.status]}
                      to={
                        hasReadCasesPermission &&
                        (!classifiedQueuesEnabled ||
                          isQueueAccessible(
                            hasReadAssignmentsPermission,
                            sessionAgent.accessible_queues,
                            filing.case.queue_id,
                            filing.case.queue_access_type,
                          ))
                          ? ROUTES_MAP.casesId.path.replace(
                              ':id',
                              String(filing.case.id),
                            )
                          : undefined
                      }
                      variant={
                        filing.case.status === 'OPEN' ? 'filled' : 'ghost'
                      }
                    >
                      {filing.case.title}
                    </U21Chip>
                  )}
                </SidebarDataRow>
                <SidebarDataRow label="Created at" showEmptyFields>
                  {formatDatetime(filing.created_at)}
                </SidebarDataRow>
                <SidebarDataRow label="Filed at" showEmptyFields>
                  {formatDate(filing.filed_at, undefined, {
                    showTimezone: true,
                    timezone: 'America/New_York',
                  })}
                </SidebarDataRow>
                <SidebarDataRow label="BSA tracking ID" showEmptyFields>
                  {filing.bsa_tracking_id}
                </SidebarDataRow>
                <SidebarDataRow label="BSA ID" showEmptyFields>
                  {filing.bsa_id}
                </SidebarDataRow>
              </U21Spacer>
            </U21Subsection>
            <SidebarCommentsCollapsible comments={filing.comments} />
          </U21Spacer>
        );
      })()}
    </U21SideMenu>
  );
};
